import { useAuth } from "@/context/auth/AuthContext";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";
import TextField from "../form-fields/TextField";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { uploadDocumentForApp } from "@/api/file";
import { useTranslation } from "react-i18next";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { isAxiosError } from "axios";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { XCircleIcon } from "lucide-react";
import { toast } from "@/hooks/use-toast";

const schema = z.object({
  title: z.string().min(1, { message: "Le nom du fichier est requis" }),
  description: z.string().optional(),
  file: z.instanceof(File),
});

type SchemaType = z.infer<typeof schema>;

const AddFileModal = ({ id, onClose = () => {} }: { id: string | undefined, onClose?: () => void }) => {
  const [open, setOpen] = useState(false);
  const { instanceData } = useAuth();
  const [loading, setLoading] = useState(false);

  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
  });

  const { t } = useTranslation();
  const vt = useVisibleT();

  const { control, getValues, setValue, trigger } = form;
  const [errorContent, setErrorContent] = useState<string | null>(null);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        setValue("file", acceptedFiles[0]);
        if (!getValues("title")) {
          setValue("title", acceptedFiles[0].name);
        }
        trigger();
      }
    },
    [setValue, getValues, trigger],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const onSubmit = async (data: SchemaType) => {
    if (id) {
      if (instanceData == null) {
        console.error("Tried to upload a Document, but instanceData is null");
        return;
      }

      try {
        setLoading(true);
        await uploadDocumentForApp(
          data.file,
          instanceData.s3Bucket,
          {
            sourceName: data.title,
            title: data.title,
            tag: "file",
            docId: id,
            mimetype: data.file.type,
            description: data.description
          },
          instanceData.id
        );
        form.reset();
        toast({
          description: t("fileUploadSucess"),
          variant: "success",
        });
        setOpen(false);
        setLoading(false);
        onClose();
      } catch (error) {
        if (isAxiosError(error)) {
          setErrorContent(error.response?.data.error);
        } else {
          setErrorContent(t("uploadError"));
        }
        setLoading(false);
      }
    }
  };

  const onOpenChange = (open: boolean) => {
    setOpen(open);
    setErrorContent(null);
    if (!open) {
      form.reset();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} key={id}>
      <DialogTrigger asChild onClick={() => setOpen(true)}>
        <Button
          type="button"
          variant="ghost"
          className="bg-white text-black font-semibold font-[Raleway] text-sm px-[12px] py-[8px] rounded-md border border-gray-300"
        >
          {t("addFile")}
        </Button>
      </DialogTrigger>
    {!errorContent ? (
      <DialogContent className="max-w-[475px] bg-white p-0" needCloseButton={!loading} preventOutsideClose={loading}>
        <DialogHeader className="border-b px-6 py-6">
          <DialogTitle>{t("addFile")}</DialogTitle>
        </DialogHeader>
        <FormProvider {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4 px-6 pb-6"
          >
            <TextField
              config={{ name: "title", type: "text", label: t('uploadFileName') }}
              control={control}
              getValues={getValues}
            />
            <TextField
              config={{
                name: "description",
                type: "text",
                label: t("Description"),
              }}
              control={control}
              getValues={getValues}
            />
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
                ${isDragActive ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-gray-400"}`}
            >
              <input {...getInputProps()} />
              {isDragActive ? (
                <p>{vt("bo.modal.dropFileHere", "Déposez le fichier ici...")}</p>
              ) : (
                <div>
                  <p>{t("uploadChooseFileOrDropFileHerePlaceholder")}</p>
                  <p className="text-sm text-gray-500 mt-2">
                    {getValues("file") &&
                      `${t("uploadSaveFile")} : ${getValues("file").name}`}
                  </p>
                </div>
              )}
            </div>
          </form>
        </FormProvider>
        <DialogFooter className="flex justify-end gap-1 border-t pt-4 pb-4 px-6">
          <Button
            className="w-full"
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={loading}
          >
            {t("formCancel")}
          </Button>
          <Button
            className="w-full"
            type="submit"
            onClick={form.handleSubmit(onSubmit)}
            disabled={!form.formState.isValid || !getValues("file")}
            loading={loading}
          >
            {loading ? t("uploadLoadingSaveFile") : t("uploadSaveFile")}
          </Button>
        </DialogFooter>
      </DialogContent>
    ) : (
      <DialogContent className="p-6">
        <VisuallyHidden>
          <DialogTitle className="sr-only">Error</DialogTitle>
        </VisuallyHidden>
        <div className="flex items-center justify-center my-4">
          <XCircleIcon className="w-8 h-8 text-red-500" />
        </div>
        <div className="text-center text-2xl mx-4">
          {t(errorContent ?? "")}
        </div>
      </DialogContent>
    )}
    </Dialog>
  );
};

export default AddFileModal;
